import { useMemo, useState } from "react";
import { AlertTriangle, Badge, Search } from "lucide-react";
import { Input } from "../../../../../../shared/components/input/Input";
import { Button } from "../../../../../../shared/components/button/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import { ScrollArea } from "../../../../../../shared/components/scroll-area/ScrollArea";

const SearchableSelect = ({
  values,
  selectedValues,
  onSelectionChange,
  placeholder = "Search and select values...",
  maxDisplayValues = 100,
  disabled = false,
  singleSelection = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredValues = useMemo(() => {
    if (!searchTerm) return values.slice(0, maxDisplayValues);
    return values
      .filter((value) => value.toLowerCase().includes(searchTerm.toLowerCase()))
      .slice(0, maxDisplayValues);
  }, [values, searchTerm, maxDisplayValues]);

  const hasMoreValues = values.length > maxDisplayValues;
  const showingCount = Math.min(filteredValues.length, maxDisplayValues);

  const toggleValue = (value) => {
    if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      if (singleSelection) {
        onSelectionChange(new Array(value));
      } else {
        onSelectionChange([...selectedValues, value]);
      }
    }
  };

  const removeValue = (value) => {
    onSelectionChange(selectedValues.filter((v) => v !== value));
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="pl-10"
          disabled={disabled}
        />
      </div>

      {isOpen && (
        <Card className="absolute z-50 w-full mt-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              Available Values ({showingCount}
              {hasMoreValues ? `/${values.length}` : ""})
            </CardTitle>
            {hasMoreValues && (
              <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md text-xs text-yellow-800">
                <AlertTriangle className="w-3 h-3" />
                <span>
                  Showing first {maxDisplayValues} of {values.length} values.
                  Use search to find specific items.
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-48">
              <div className="space-y-1">
                {filteredValues.map((value) => (
                  <div
                    key={value}
                    className={`p-2 rounded cursor-pointer text-sm hover:bg-accent ${selectedValues.includes(value) ? "bg-accent" : ""}`}
                    onClick={() => toggleValue(value)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate flex-1">{value + ""}</span>
                      {selectedValues.includes(value) && (
                        <Badge variant="secondary" className="ml-2">
                          Selected
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {filteredValues.length === 0 && (
                  <div className="p-4 text-center text-muted-foreground">
                    No values found matching "{searchTerm}"
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchableSelect;
